import { Button, Typography } from '@breetometer/components';
import { DollarCryptoIcon, InfoIcon } from 'BreetAssets';
import { FiatInput, type IFiatDropDownOption, ImageComponent } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	cleanRate,
	convertCryptoInputAmount,
	convertFxRate,
	type CURRENCYCODETYPE,
	formatCurrencyInputAmount,
	FXRATE,
	getCleanedValue,
	handleDecimalPoints,
	type ISellAssets,
	toFixedPrecision,
} from 'BreetHelpers';
import { useCurrencyOutput, useCurrentFxRate, useFormData, useRateCalculatorContext, useUserCurrencyProps } from 'BreetHooks';
import { useGetMarketAssetConverterQuery, useGetSellAssetsQuery } from 'BreetRedux';
import { type ChangeEvent, memo, useCallback, useEffect, useMemo } from 'react';
import type { FieldPath } from 'react-hook-form';
import type * as yup from 'yup';

import { RateCalculatorSellCryptoSchema } from '../../DialogRateCalculator.helpers';
import { useRateCalculatorFormStageContext } from '../FormStageContext';

const DEFAULTASSETSYMBOL = 'BTC';

const getSelectedAssetOption = (dataArg: ISellAssets) => ({
	label: dataArg.name,
	icon: (
		<ImageComponent
			src={dataArg.avatar as string}
			alt={dataArg.name}
		/>
	),
});

const FormSellCryptoComponent = () => {
	const { navigator } = useRateCalculatorFormStageContext();

	const { setSelectedAsset, selectedAsset } = useRateCalculatorContext();

	const currencyOutput = useCurrencyOutput();

	const currencyProps = useUserCurrencyProps();

	const currentFxRate = useCurrentFxRate({ fxRate: FXRATE.DEFAULT });

	const { data: tradeSellAssetsData } = useGetSellAssetsQuery(undefined, alwaysRefetchApiQueryOptions);

	const defaultOption = useMemo(() => {
		const data = tradeSellAssetsData?.data?.find((asset) => asset.symbol === DEFAULTASSETSYMBOL);
		if (!data) return undefined;
		if (selectedAsset) return getSelectedAssetOption(selectedAsset);
		return getSelectedAssetOption(data);
	}, [selectedAsset, tradeSellAssetsData?.data]);

	useEffect(() => {
		if (selectedAsset) return;
		const data = tradeSellAssetsData?.data?.find((asset) => asset.symbol === DEFAULTASSETSYMBOL);
		if (data) setSelectedAsset(data);
	}, [selectedAsset, setSelectedAsset, tradeSellAssetsData?.data]);

	const selectedSymbol: string = useMemo(() => {
		const asset = tradeSellAssetsData?.data?.find((assetItem) => assetItem.name === selectedAsset?.name);

		return asset?.symbol ?? DEFAULTASSETSYMBOL;
	}, [selectedAsset, tradeSellAssetsData?.data]);

	const { data: marketAssetConverterData, isFetching: isMarketAssetConverterLoading } = useGetMarketAssetConverterQuery(
		{ to: 'usd', from: selectedSymbol },
		{ skip: !selectedSymbol }
	);

	const assetOptions = useMemo(
		() =>
			tradeSellAssetsData?.data?.map((asset) => ({
				label: asset.name,
				icon: (
					<ImageComponent
						src={asset.avatar as string}
						alt={asset.name}
					/>
				),
			})) ?? [],
		[tradeSellAssetsData?.data]
	);

	const selectCrypto = useCallback(
		(option: IFiatDropDownOption) => {
			const selectedAssets = tradeSellAssetsData?.data?.find((asset) => asset.name.toLowerCase() === option.label.toLowerCase());
			setSelectedAsset(selectedAssets);
		},
		[tradeSellAssetsData?.data, setSelectedAsset]
	);

	const cryptoRate = marketAssetConverterData?.data?.quote.USD.price;

	const {
		hookFormProps: { watch, setValue },
	} = useFormData(
		RateCalculatorSellCryptoSchema,
		{
			currencyAmount: '1',
			cryptoAmount: '0',
		},
		{ mode: 'onChange' }
	);

	const handleInputChange = useCallback(
		(field: FieldPath<yup.InferType<typeof RateCalculatorSellCryptoSchema>>) => (event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			let cleanedValue = getCleanedValue(value);

			cleanedValue = handleDecimalPoints(cleanedValue);

			setValue(field, cleanedValue);

			if (!cryptoRate) return '0';

			if (field === 'currencyAmount') {
				const cryptoResult = convertFxRate(cleanRate(cryptoRate.toString()), cleanRate(cleanedValue), 'to');
				setValue('cryptoAmount', toFixedPrecision(cryptoResult, 7));
			}

			if (field === 'cryptoAmount') {
				const currencyResult = convertFxRate(cleanRate(cleanedValue), cleanRate(cryptoRate.toString()), 'from');
				setValue('currencyAmount', toFixedPrecision(currencyResult, 2));
			}
		},
		[setValue, cryptoRate]
	);

	const cryptoAmount = useMemo(() => {
		const cryptoAmountValue = watch('cryptoAmount');

		if (cryptoAmountValue === undefined) {
			if (!cryptoRate) return '0';
			const defaultCryptoResult = convertFxRate(cleanRate(cryptoRate.toString()), 1, 'to');
			return toFixedPrecision(defaultCryptoResult, 7);
		}

		return cryptoAmountValue;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cryptoRate, watch('cryptoAmount')]);

	const receiveAmount = useMemo(() => {
		const usdAmountValue = watch('currencyAmount');

		if (!usdAmountValue) return '0';

		if (!currentFxRate.rate) {
			console.error('No rate found');
			return '0';
		}

		const value = convertFxRate(cleanRate(currentFxRate.rate), cleanRate(usdAmountValue), 'from');

		return value;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cryptoRate, watch('currencyAmount')]);

	useEffect(() => {
		if (!cryptoRate) return;

		const currencyAmount = watch('currencyAmount');

		if (!currencyAmount) return;

		const cryptoResult = convertFxRate(cleanRate(cryptoRate.toString()), cleanRate(currencyAmount), 'to');
		setValue('cryptoAmount', toFixedPrecision(cryptoResult, 7));
	}, [cryptoRate, setValue, watch]);

	return (
		<div className='rate-calculator-form'>
			<FiatInput.Default
				label='Enter Currency Amount'
				currencyCode='USD'
				icon={<DollarCryptoIcon />}
				hideTooltip
				onChange={handleInputChange('currencyAmount')}
				value={formatCurrencyInputAmount(watch('currencyAmount') ?? '0', 'USD')}
				loading={isMarketAssetConverterLoading}
			/>

			<FiatInput.Default
				label='Enter Crypto Amount'
				currencyCode='USD'
				onChange={handleInputChange('cryptoAmount')}
				value={convertCryptoInputAmount(cryptoAmount)}
				hideTooltip
				loading={isMarketAssetConverterLoading}
				wrapperClass='crypto-amount'
				dropDown={{
					defaultOption,
					selectedOption: selectedAsset && getSelectedAssetOption(selectedAsset),
					options: assetOptions,
					onSelect: selectCrypto,
					isLoading: isMarketAssetConverterLoading,
				}}
			/>

			<div className='receive-input-container'>
				<FiatInput.Default
					label='You Receive'
					hideTooltip
					currencyCode={currencyProps?.code as unknown as CURRENCYCODETYPE}
					value={currencyOutput(receiveAmount)}
					readOnly
					aria-readonly
					loading={isMarketAssetConverterLoading}
				/>
			</div>

			<div className='rate-calculator-note'>
				<Typography>All payouts are subject to a transaction fee.</Typography>
			</div>

			<div className='buycrypto-rate bg-primary-600 border-dark-navy'>
				<Typography className='color-primary-100'>
					<span className={isMarketAssetConverterLoading ? 'global-skeleton' : ''}>
						1 {selectedSymbol} = USD {currencyOutput(marketAssetConverterData?.data?.quote.USD.price)}
					</span>
				</Typography>
			</div>

			<div className='buycrypto-rate-hiw'>
				<InfoIcon
					variant='mini-wallet'
					className='svg-dark'
				/>
				<Typography
					as='a'
					href='https://help.breet.io/en/articles/15014891-how-can-my-business-check-crypto-to-cash-rates-on-the-dashboard'
					target='_blank'
					rel='noopener noreferrer'
				>
					How it works
				</Typography>
			</div>

			<Button
				type='button'
				onClick={navigator.goTo('asset-details')}
				isLoading={isMarketAssetConverterLoading}
			>
				Sell Crypto
			</Button>
		</div>
	);
};

const FormSellCrypto = memo(FormSellCryptoComponent);

export default FormSellCrypto;
